import React, { useEffect, useState } from 'react';
import {
  Target, Play, Loader2, AlertTriangle, ShieldAlert, Store, User as UserIcon,
  CheckCircle2, UserCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import { getSupervisors } from '../lib/api';

const CATEGORY_LABELS = {
  TIENDA_CRITICA: 'Tienda crítica',
  MERCADERISTA_HIGH_RISK: 'Mercaderista high-risk',
  STOCK_REINCIDENTE: 'Stock reincidente',
  VISITA_DEFICIENTE: 'Visita deficiente',
};

const SEVERITY_COLOR = {
  CRITICAL: 'destructive',
  HIGH: 'destructive',
  MEDIUM: 'default',
  LOW: 'secondary',
};

const SEVERITY_BAR = {
  CRITICAL: 'bg-red-600',
  HIGH: 'bg-orange-500',
  MEDIUM: 'bg-yellow-500',
  LOW: 'bg-slate-400',
};

const CATEGORY_ICON = {
  TIENDA_CRITICA: Store,
  MERCADERISTA_HIGH_RISK: UserIcon,
  STOCK_REINCIDENTE: AlertTriangle,
  VISITA_DEFICIENTE: ShieldAlert,
};

export default function PriorityCenterPage() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState('all');

  // Assign dialog
  const [assignFor, setAssignFor] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState('');

  // Resolve dialog
  const [resolveFor, setResolveFor] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        api.get('/priorities/stats'),
        api.get('/priorities', { params: { limit: 200 } }),
      ]);
      setStats(statsRes.data);
      setItems(listRes.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cargar prioridades');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    getSupervisors()
      .then((res) => setSupervisors(res.data || []))
      .catch(() => setSupervisors([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await api.post('/priorities/run');
      const summary = res.data?.summary || {};
      const total = Object.values(summary).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Centro de Prioridades recalculado: ${total} grupo(s).`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error recalculando');
    } finally {
      setRunning(false);
    }
  };

  const confirmAssign = async () => {
    if (!assignFor || !selectedOwner) return;
    try {
      await api.post(`/priorities/${assignFor.id}/assign`, { owner_user_id: selectedOwner });
      toast.success('Asignado correctamente');
      setAssignFor(null);
      setSelectedOwner('');
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error asignando');
    }
  };

  const confirmResolve = async () => {
    if (!resolveFor) return;
    try {
      await api.post(`/priorities/${resolveFor.id}/resolve`, {
        resolution_notes: resolutionNotes || null,
      });
      toast.success('Marcada como resuelta');
      setResolveFor(null);
      setResolutionNotes('');
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error resolviendo');
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const filterFn = (it) => {
    if (it.status === 'RESOLVED') return false;
    if (tab === 'all') return true;
    if (tab === 'critical') return it.severity === 'CRITICAL';
    if (tab === 'stores') return it.category === 'TIENDA_CRITICA';
    if (tab === 'merc_risk') return it.category === 'MERCADERISTA_HIGH_RISK';
    return true;
  };

  const filtered = items.filter(filterFn);

  return (
    <div className="space-y-6" data-testid="priority-center-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Centro de Prioridades</h1>
            <p className="text-sm text-slate-500">
              Acciones críticas priorizadas por score · {stats?.open_total ?? 0} abierta(s)
            </p>
          </div>
        </div>
        <Button onClick={runNow} disabled={running} className="gap-2" data-testid="priorities-run-btn">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Recalcular
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label="Críticas"
          value={stats?.by_severity?.CRITICAL ?? 0}
          icon={AlertTriangle}
          accent="text-red-600"
          testId="kpi-critical"
        />
        <KpiCard
          label="Altas"
          value={stats?.by_severity?.HIGH ?? 0}
          icon={ShieldAlert}
          accent="text-orange-600"
          testId="kpi-high"
        />
        <KpiCard
          label="Medias"
          value={stats?.by_severity?.MEDIUM ?? 0}
          icon={Target}
          accent="text-yellow-600"
          testId="kpi-medium"
        />
        <KpiCard
          label="Bajas"
          value={stats?.by_severity?.LOW ?? 0}
          icon={CheckCircle2}
          accent="text-slate-600"
          testId="kpi-low"
        />
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">Todas ({items.filter(i => i.status !== 'RESOLVED').length})</TabsTrigger>
          <TabsTrigger value="critical" data-testid="tab-critical">Críticas</TabsTrigger>
          <TabsTrigger value="stores" data-testid="tab-stores">Tiendas críticas</TabsTrigger>
          <TabsTrigger value="merc_risk" data-testid="tab-merc-risk">Merc. high-risk</TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          <div className="space-y-3 mt-4">
            {filtered.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-slate-500 text-sm">
                  {loading
                    ? 'Cargando…'
                    : 'No hay prioridades abiertas en este filtro. ¡Buen trabajo!'}
                </CardContent>
              </Card>
            ) : (
              filtered.map((p) => (
                <PriorityCard
                  key={p.id}
                  p={p}
                  onAssign={() => setAssignFor(p)}
                  onResolve={() => setResolveFor(p)}
                  formatDate={formatDate}
                />
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog: Asignar */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignar responsable</DialogTitle>
            <DialogDescription>{assignFor?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Select value={selectedOwner} onValueChange={setSelectedOwner}>
              <SelectTrigger data-testid="assign-owner-select">
                <SelectValue placeholder="Selecciona supervisor…" />
              </SelectTrigger>
              <SelectContent>
                {supervisors.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignFor(null)}>Cancelar</Button>
            <Button onClick={confirmAssign} disabled={!selectedOwner} data-testid="assign-confirm-btn">
              Asignar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Resolver */}
      <Dialog open={!!resolveFor} onOpenChange={(o) => !o && setResolveFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marcar como resuelta</DialogTitle>
            <DialogDescription>{resolveFor?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Textarea
              placeholder="Notas de resolución (opcional)…"
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              data-testid="resolve-notes-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveFor(null)}>Cancelar</Button>
            <Button onClick={confirmResolve} data-testid="resolve-confirm-btn">
              Resolver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== Componentes auxiliares ==============

function KpiCard({ label, value, icon: Icon, accent, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
            <p className={`text-3xl font-bold mt-2 ${accent}`}>{value}</p>
          </div>
          <Icon className={`w-8 h-8 ${accent} opacity-50`} />
        </div>
      </CardContent>
    </Card>
  );
}

function PriorityCard({ p, onAssign, onResolve, formatDate }) {
  const Icon = CATEGORY_ICON[p.category] || Target;
  const bar = SEVERITY_BAR[p.severity] || 'bg-slate-400';
  return (
    <Card data-testid={`priority-${p.id}`}>
      <div className={`h-1 w-full ${bar} rounded-t-lg`} />
      <CardContent className="pt-4">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${bar} text-white shrink-0`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge variant={SEVERITY_COLOR[p.severity]} className="uppercase">
                {p.severity}
              </Badge>
              <Badge variant="outline">{CATEGORY_LABELS[p.category] || p.category}</Badge>
              <span className="text-xs font-mono text-slate-500">score {p.priority_score}</span>
              <span className="text-xs text-slate-400">· {formatDate(p.created_at)}</span>
              {p.status === 'ASSIGNED' && (
                <Badge variant="default" className="bg-blue-600">Asignada</Badge>
              )}
            </div>
            <p className="font-semibold text-slate-900">{p.title}</p>
            <p className="text-sm text-slate-600 mt-1">{p.description}</p>
            {p.suggested_action && (
              <p className="text-sm text-emerald-700 mt-2">
                <span className="font-medium">→ Acción sugerida: </span>
                {p.suggested_action}
              </p>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
              {p.store_name && <span>🏪 {p.store_name}</span>}
              {p.user_name && <span>👤 {p.user_name}</span>}
              {p.owner_name && (
                <span className="flex items-center gap-1 text-blue-600">
                  <UserCheck className="w-3 h-3" /> Responsable: {p.owner_name}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={onAssign} data-testid={`assign-btn-${p.id}`}>
              Asignar
            </Button>
            <Button size="sm" onClick={onResolve} data-testid={`resolve-btn-${p.id}`}>
              Resolver
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
